import React, { useCallback, useContext, useEffect, useRef } from 'react'
import { StoreDispatch, StoreState } from '../../Store'

// Components
import ContainedWrap from '../ContainedWrap'
import Container from '../Container'
import Grid from '../Grid'
import GridItem from '../GridItem'
import RichText from '../RichText'
import ScrollSection from '../ScrollSection'
import Spacer from '../Spacer'
import Video from '../Video'
import AnimateLetterbox from '../animation/AnimateLetterbox'
import AnimateSlideText from '../animation/AnimateSlideText'
import AnimateSplitText from '../animation/AnimateSplitText'
// Vars
import { Cta, Title } from '../TextStyles'

// SVGs
import DottedVerticalLine from '../svgs/DottedVerticalLine'

// Styles
import { scrollToId } from '../../utils/utils'
import {
  VideoHeroExploreLink,
  VideoHeroInner,
  VideoHeroMain,
  VideoHeroTitle,
  VideoHeroVimeo,
  VideoHeroWrapper,
} from './index.style'

const VideoHero = ({ id, tagline, heading, image, videoUrl, explore }) => {
  const maxDelayTimeout = useRef(null)
  const $video = useRef(null)

  const store = useContext(StoreState)
  const dispatch = useContext(StoreDispatch)

  const { waiting } = store

  const videoIsReady = useCallback(() => {
    if (maxDelayTimeout.current) clearTimeout(maxDelayTimeout.current)
    dispatch({ type: 'END_INITIAL_WAIT' })
    dispatch({ type: 'HIDE_TRANSITION_MASK' })
  }, [dispatch, maxDelayTimeout])

  const initialDelay = 2

  useEffect(() => {
    dispatch({ type: 'BEGIN_INITIAL_WAIT' })
    maxDelayTimeout.current = setTimeout(videoIsReady, 3000)

    return () => {
      if (maxDelayTimeout.current) clearTimeout(maxDelayTimeout.current)
    }
  }, [dispatch, maxDelayTimeout, videoIsReady])

  useEffect(() => {
    const video = $video.current

    video.addEventListener('playing', videoIsReady)

    return () => {
      video.removeEventListener('playing', videoIsReady)
    }
  }, [videoIsReady])

  return (
    <ScrollSection>
      <VideoHeroWrapper>
        <VideoHeroVimeo visible={!waiting}>
          <AnimateLetterbox
            id={`${id}-video`}
            duration={2.4}
            delay={initialDelay}
            scaledX={0.75}
            scaledY={0.55}
            scaleIn={true}
            syncWithScroll={false}
            useInView={false}
            isInView={!waiting}
          >
            <Video ref={$video} src={videoUrl} />
          </AnimateLetterbox>
        </VideoHeroVimeo>
        <VideoHeroMain visible={!waiting}>
          <Container>
            <Grid>
              <GridItem
                tabletP={8}
                tabletPStart={3}
                desk={6}
                deskStart={4}
                tinyAlign={`center`}
              >
                <VideoHeroInner>
                  <VideoHeroTitle>
                    <Title>
                      <AnimateSplitText
                        id={`${id}-tagline`}
                        delay={initialDelay}
                        useInView={false}
                        isInView={!waiting}
                      >
                        {tagline}
                      </AnimateSplitText>
                    </Title>
                  </VideoHeroTitle>

                  <Spacer size={[15, 30]} />

                  <ContainedWrap size={820}>
                    <RichText
                      id={`${id}-heading`}
                      content={heading}
                      forceHeading
                      superHeading
                      delay={initialDelay + 0.1}
                      useInView={false}
                      isInView={!waiting}
                    />
                  </ContainedWrap>
                </VideoHeroInner>
              </GridItem>
            </Grid>
            <VideoHeroExploreLink>
              <Cta as="button" onClick={() => scrollToId('summary', dispatch)}>
                <AnimateSplitText
                  id={`${id}-explore`}
                  delay={initialDelay + 1.5}
                  useInView={false}
                  isInView={!waiting}
                >
                  {explore}
                </AnimateSplitText>
              </Cta>
              <AnimateSlideText
                id={`${id}-explore-line`}
                delay={initialDelay + 1}
                useInView={false}
                isInView={!waiting}
              >
                <DottedVerticalLine />
              </AnimateSlideText>
            </VideoHeroExploreLink>
          </Container>
        </VideoHeroMain>
      </VideoHeroWrapper>
    </ScrollSection>
  )
}

export default VideoHero
